import { useCallback, useState } from 'react';
import { useStore } from '@nanostores/react';
import { toast } from 'react-toastify';
import { apiDownloadCode, TURN_IN_FLIGHT_CODE } from '~/lib/api/export';
import { chatId } from '~/lib/persistence/useChatHistory';
import { blockAndShowPlans, handlePlanRequired, usePlanAccess } from '~/lib/planGate';

/**
 * The one "Download code" behaviour, shared by every entry point (the Code tab's
 * button and the tools launcher row) so they can't drift into two different
 * gates or two different error messages.
 *
 * The gate: free opens the plans dialog instead of downloading; every paid plan,
 * Starter included, downloads freely and as often as it likes. The server
 * enforces it — a plan that is still loading lets the click through rather than
 * showing a paying user a locked button.
 */
export function useCodeDownload() {
  const currentChatId = useStore(chatId);
  const { tier, isFree, planUnknown } = usePlanAccess();
  const [busy, setBusy] = useState(false);

  const download = useCallback(async () => {
    if (busy || !currentChatId) {
      return;
    }

    if (isFree) {
      // Short-circuits before apiDownloadCode, so the server's own
      // publish_blocked (export.controller.ts) never gets the chance to fire.
      blockAndShowPlans('export', { tier, chatId: currentChatId });
      return;
    }

    setBusy(true);

    try {
      await apiDownloadCode(currentChatId);
    } catch (err: any) {
      if (handlePlanRequired(err)) {
        return;
      }

      const body = err?.response?.data;

      toast.error(
        body?.code === TURN_IN_FLIGHT_CODE
          ? 'Dunea is still building this project. Try again when it finishes.'
          : body?.message || 'Could not package your code. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }, [busy, currentChatId, isFree, tier]);

  return { download, busy, isFree, planUnknown, available: !!currentChatId };
}
